import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, Globe, MapPin } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "../services/apiClient";

const Developers = () => {
  const navigate = useNavigate();
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDevelopers();
  }, []);

  const fetchDevelopers = async () => {
    try {
      const response = await api.get("/developers", { auth: false });

      let data = null;
      try {
        data = await response.json();
      } catch (_parseError) {
        data = null;
      }

      if (!response.ok) {
        const detail = String(data?.detail || data?.message || "").toLowerCase();
        const isNoData =
          response.status === 404 ||
          (detail.includes("no hay") && detail.includes("desarrollador"));

        if (isNoData) {
          setDevelopers([]);
          return;
        }

        throw new Error(data?.detail || data?.message || "Error al cargar desarrolladores");
      }

      setDevelopers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching developers:", error);
      toast.error(error?.message || "Error al cargar desarrolladores");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />

      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              data-testid="developers-title"
            >
              Desarrolladores Inmobiliarios
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
              Conoce a los profesionales que están transformando el mercado
              inmobiliario en México
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : developers.length === 0 ? (
          <div className="text-center py-12 md:py-20">
            <Building2 className="w-12 h-12 md:w-16 md:h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-base md:text-lg text-muted-foreground mb-6">
              Aún no hay desarrolladores registrados
            </p>
            <Button onClick={() => navigate("/register")}>
              Convertirse en Desarrollador
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {developers.map((developer, index) => (
              <motion.div
                key={developer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => navigate(`/developer/${developer.id}`)}
                className="bg-background border border-border/40 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 transition-all duration-300 cursor-pointer"
                data-testid={`developer-card-${developer.id}`}
              >
                {developer.image_url && (
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={developer.image_url}
                      alt={developer.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-4 md:p-6">
                  <h3
                    className="text-lg md:text-xl font-bold mb-2 line-clamp-2"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {developer.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                    {developer.description}
                  </p>

                  <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-muted-foreground mb-4">
                    {developer.years_active && (
                      <span className="flex items-center">
                        <Building2 className="w-4 h-4 mr-1" />
                        {developer.years_active} años
                      </span>
                    )}
                    {developer.website && (
                      <span className="flex items-center">
                        <Globe className="w-4 h-4 mr-1" />
                        Sitio web
                      </span>
                    )}
                  </div>

                  {developer.specialties &&
                    developer.specialties.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {developer.specialties
                          .slice(0, 3)
                          .map((specialty, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1 bg-accent/10 text-accent rounded-full text-xs font-medium"
                            >
                              {specialty}
                            </span>
                          ))}
                      </div>
                    )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Developers;
